// One-shot fetcher for GET /api/me. Returns the authenticated user and
// their memberships, shaped by @oracord/shared.
//
// Deliberately skipOrgHeader:true — /api/me is the HOW WE DISCOVER the
// user's orgs. Sending an org header before we have one is backwards.

import { useEffect, useState } from 'react';
import type { MeResponse } from '@oracord/shared';
import { apiFetch, ApiError } from '../lib/api';

interface UseMeResult {
  me: MeResponse | null;
  loading: boolean;
  error: ApiError | null;
  reload: () => Promise<void>;
}

export function useMe(): UseMeResult {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<MeResponse>('/api/me', { skipOrgHeader: true });
      setMe(data);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'Network error', err));
      setMe(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // Deliberately once-on-mount. Consumers call `reload` after actions
    // that mutate memberships (org create, invitation accept, etc.).

  }, []);

  return { me, loading, error, reload: load };
}
